'use client'

import {
  type OptionalRestArgsOrSkip,
  type PaginatedQueryArgs,
  type PaginatedQueryReference,
  type RequestForQueries,
  type UsePaginatedQueryReturnType,
  useConvexAuth,
  useQueries,
} from 'convex/react'
import { type FunctionReference, getFunctionName } from 'convex/server'
import { convexToJson, type Value } from 'convex/values'
// Cache-aware drop-in replacements (see `ConvexQueryCacheProvider` in
// `components/convex-provider.tsx`): subscriptions stay warm for a few
// minutes after the last subscriber unmounts, so navigation back to a
// recently-viewed chat/canvas re-paints instantly instead of
// cold-resubscribing over the websocket.
import { usePaginatedQuery, useQuery } from 'convex-helpers/react/cache/hooks'
import { useEffect, useMemo } from 'react'

/**
 * Auth-gated replacements for `useQuery` / `useQueries` /
 * `usePaginatedQuery`.
 *
 * WHY THIS EXISTS
 * ---------------
 * `ConvexProviderWithAuth` attaches the named Clerk JWT to the Convex socket
 * ASYNCHRONOUSLY after the client mounts. There is a brief bootstrap
 * window where `ctx.auth.getUserIdentity()` is still `null`. Every
 * org/user-scoped Convex query now hard-rejects unauthenticated callers
 * (`requireOrgAccess` etc. throw `NOT_AUTHENTICATED`), so a query that
 * fires on mount during that window throws and crashes the React tree.
 *
 * These hooks hold the query (return `undefined` / skipped) until Convex
 * reports an authenticated identity, then run it exactly as `useQuery`
 * would. They are a no-op once auth has attached, so they are safe to use
 * even for queries that only fire behind user interaction.
 *
 * USAGE RULE (enforced by `tests/convex-client-auth-gating.test.ts`)
 * ------------------------------------------------------------------
 * Any client reactive query whose target Convex function is identity-scoped
 * (guarded by `requireOrgAccess`, `requireUserAccess`,
 * `requireCallerOrgScope`, or a row-level
 * org/user check) MUST use these hooks. Truly public functions (e.g. the
 * template gallery, the public unsubscribe page) keep plain `useQuery`.
 *
 * The `'skip'` sentinel is preserved: callers that already pass `'skip'`
 * stay skipped; callers that pass args are skipped only until authed.
 */
export function useAuthedQuery<Query extends FunctionReference<'query'>>(
  query: Query,
  ...args: OptionalRestArgsOrSkip<Query>
): Query['_returnType'] | undefined {
  const { isAuthenticated } = useConvexAuth()
  const skipArgs = ['skip'] as unknown as OptionalRestArgsOrSkip<Query>
  return useQuery(query, ...(isAuthenticated ? args : skipArgs))
}

/** Auth-gated dynamic-query sibling of {@link useAuthedQuery}. */
export function useAuthedQueries(
  queries: RequestForQueries
): Record<string, unknown | undefined> {
  const { isAuthenticated } = useConvexAuth()
  const results = useQueries(isAuthenticated ? queries : {})
  for (const result of Object.values(results)) {
    if (result instanceof Error) {
      throw result
    }
  }
  return results
}

export type SoftAuthedQueryResult<Data> = {
  /** `undefined` while skipped, unauthenticated, loading, or errored. */
  data: Data | undefined
  /** True while the current subscription's latest result is an error. */
  isError: boolean
}

/**
 * One console report per function per page lifetime — a persistently
 * failing shell query would otherwise log on every reactive update.
 */
const reportedSoftQueryErrors = new Set<string>()

/**
 * The one RUM-captured sink for a shell subscription that degraded instead
 * of throwing (#952): `console.error`, once per function per page lifetime.
 * Shared with the fail-soft boundaries that wrap subscriptions
 * `useSoftAuthedQuery` cannot (paginated ones), so every degraded shell
 * query is reported the same way from the same approved file.
 */
export function reportSoftQueryFailure(
  queryName: string,
  error: unknown,
  surface = 'useSoftAuthedQuery'
): void {
  if (reportedSoftQueryErrors.has(queryName)) {
    return
  }
  reportedSoftQueryErrors.add(queryName)
  console.error(
    `[${surface}] ${queryName} failed — degrading to empty result.`,
    error
  )
}

/** Stable identity for every skipped/unauthenticated render. */
const EMPTY_QUERY_REQUEST: RequestForQueries = {}

/**
 * Fail-soft sibling of {@link useAuthedQuery} for subscriptions mounted
 * on page-load-critical shells (app header, command palette, nav chrome).
 *
 * `useQuery`/`useAuthedQuery` THROW a server-side query error into the
 * React render, and the workspace shell has no error boundary above the
 * header — so one failing auxiliary query white-screens every page. The
 * 2026-08-14 outage was exactly this: a `convex deploy` from a stale
 * pre-merge branch checkout replaced prod's function set while the newer
 * frontend was live, every open tab's palette subscription to the
 * now-missing `globalSearch/queries:listRecentEmails` errored, and every
 * authed page fell to `app/error.tsx`. Bundle↔backend function-set skew
 * is a NORMAL, recurring state (deploys, rollbacks, stale tabs, stray
 * pushes), so a shell subscription must degrade to "no data", never take
 * down the tree.
 *
 * Semantics: auth-gated and `'skip'`-preserving like every sibling. A
 * server error surfaces as `{ data: undefined, isError: true }` (reported
 * once per function via `console.error`, which Datadog RUM captures) so
 * callers can settle loading states instead of spinning forever. Uses the
 * raw (uncached) `useQueries` under the hood, so results cold-resubscribe
 * on remount — fine for shell surfaces, whose queries are cheap.
 */
export function useSoftAuthedQuery<Query extends FunctionReference<'query'>>(
  query: Query,
  ...args: OptionalRestArgsOrSkip<Query>
): SoftAuthedQueryResult<Query['_returnType']> {
  const { isAuthenticated } = useConvexAuth()
  const skip = !isAuthenticated || args[0] === 'skip'
  const argsObject = args[0] === 'skip' ? {} : (args[0] ?? {})
  const queryName = getFunctionName(query)
  const argsJson = JSON.stringify(convexToJson(argsObject as Value))
  // Key the request by (function name, serialized args): the caller's
  // inline args object and the `api.…` proxy reference change identity
  // every render, and an identity-keyed request would resubscribe
  // `useQueries` each render. Mirrors the upstream `useQuery` memoization.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by queryName + argsJson in place of the unstable query/argsObject identities
  const request = useMemo<RequestForQueries>(() => {
    if (skip) {
      return EMPTY_QUERY_REQUEST
    }
    return { value: { query, args: argsObject as Record<string, Value> } }
  }, [skip, queryName, argsJson])
  const results = useQueries(request)
  const result = results.value as Query['_returnType'] | Error | undefined
  const isError = result instanceof Error

  useEffect(() => {
    if (isError) {
      reportSoftQueryFailure(queryName, result)
    }
  }, [isError, queryName, result])

  return useMemo(
    () => ({
      data: isError ? undefined : (result as Query['_returnType'] | undefined),
      isError,
    }),
    [result, isError]
  )
}

export function useAuthedPaginatedQuery<Query extends PaginatedQueryReference>(
  query: Query,
  args: PaginatedQueryArgs<Query> | 'skip',
  options: {
    initialNumItems: number
    /**
     * REQUIRED (true) when the server paginates via convex-helpers
     * `stream`/`paginator` instead of the native `.paginate()`: those
     * don't participate in the query journal, so the hook must pin each
     * page's `endCursor` itself or reactive updates open holes/overlaps
     * at page seams (rows silently vanish as new events arrive).
     */
    customPagination?: boolean
  }
): UsePaginatedQueryReturnType<Query> {
  const { isAuthenticated } = useConvexAuth()
  return usePaginatedQuery(query, isAuthenticated ? args : 'skip', options)
}
